import type { TFunction } from 'i18next'
import type { OneTrustCookieItem } from '@/blocks/CookieListBlock/CookieListBlock.types'

export function getCookiePurpose(item: OneTrustCookieItem): string {
  return item.defaultDescription ?? item.domainCookieInfoDtoList[0]?.description ?? ''
}

export type CookieCategoryGroup = {
  categoryName: string
  categoryId: string
  items: Array<OneTrustCookieItem>
}

export function groupCookiesByCategory(content: Array<OneTrustCookieItem>): Array<CookieCategoryGroup> {
  const map = new Map<string, CookieCategoryGroup>()
  for (const item of content) {
    const categoryName = item.defaultCategory || item.domainCookieInfoDtoList[0]?.displayGroupName || ''
    const categoryId = item.domainCookieInfoDtoList[0]?.cookieCategoryID || ''

    if (!KNOWN_CATEGORY_IDS.has(categoryId)) continue

    const existing = map.get(categoryName)
    if (existing) {
      existing.items.push(item)
    } else {
      map.set(categoryName, { categoryName, categoryId, items: [item] })
    }
  }
  const groups = Array.from(map.values())
  groups.sort((a, b) => {
    const indexA = CATEGORY_ORDER.indexOf(a.categoryId as (typeof CATEGORY_ORDER)[number])
    const indexB = CATEGORY_ORDER.indexOf(b.categoryId as (typeof CATEGORY_ORDER)[number])
    return indexA - indexB
  })
  return groups
}

const CATEGORY_ORDER = ['C0001', 'C0002', 'C0003', 'C0004', 'C0005'] as const
const KNOWN_CATEGORY_IDS = new Set<string>(CATEGORY_ORDER)

const CATEGORY_DESCRIPTION_KEYS = {
  C0001: 'COOKIE_CATEGORY_DESC_C0001',
  C0002: 'COOKIE_CATEGORY_DESC_C0002',
  C0003: 'COOKIE_CATEGORY_DESC_C0003',
  C0004: 'COOKIE_CATEGORY_DESC_C0004',
  C0005: 'COOKIE_CATEGORY_DESC_C0005',
} as const

export function getCategoryDescriptionKey(categoryId: string) {
  return (
    CATEGORY_DESCRIPTION_KEYS[categoryId as keyof typeof CATEGORY_DESCRIPTION_KEYS] ?? 'COOKIE_CATEGORY_DESC_UNKNOWN'
  )
}

const LIFESPAN_KEYS = {
  PERSISTENT: 'COOKIE_LIFESPAN_PERSISTENT',
  SESSION: 'COOKIE_LIFESPAN_SESSION',
} as const

export function getLifespanKey(lifespan: string) {
  // OneTrust only emits PERSISTENT or SESSION; fall back to PERSISTENT for any unknown value
  // since "always present" is the safer assumption to surface to users.
  return LIFESPAN_KEYS[lifespan as keyof typeof LIFESPAN_KEYS] ?? 'COOKIE_LIFESPAN_PERSISTENT'
}

// OneTrust returns expiry as a human-readable string (e.g. "0 days", "30 days", "1 year").
// Cookies that live less than 24h come through as "0 days", which looks confusing to users.
// Map those to a localized "< 1 day" label instead.
const ZERO_DAYS_PATTERN = /^0\s+days?\b/i

export function formatExpiry(expiry: string, t: TFunction<'cookieConsent'>): string {
  if (ZERO_DAYS_PATTERN.test(expiry.trim())) {
    return t('COOKIE_EXPIRY_LESS_THAN_DAY')
  }
  return expiry
}
